import {createSlice, isAnyOf} from '@reduxjs/toolkit';
import {
    getInitialFacilities,
    setFilteredFacilities,
    getFacilityDetailsData,
    updateSelectedFacilityId,
    getFilterOptionsData,
    updateAppliedFilters,
    clearAppliedFilters,
    getPaginatedFacilitiesData,
    getSearchedFacilitiesData,
    setFacilitySearchText,
    ERRORS,
    updateFilteredFacilities,
    updateFacilityHasCreatedEventRates,
} from './facilities-actions';
import {
    facilitiesAdapter,
    filteredFacilitiesAdapter,
} from './facilities-adapters';

const initialState = {
    canonicalFacilities: facilitiesAdapter.getInitialState({
        isLoading: false,
        error: null,
    }),
    facilities: facilitiesAdapter.getInitialState({
        isLoading: false,
        error: null,
    }),
    meta: {
        limit: 25,
        offset: 0,
        nextOffset: 25,
        total: 25,
        totalFacilityCount: 25,
        totalFilteredFacilityCount: 25,
    },
    filteredFacilities: filteredFacilitiesAdapter.getInitialState({
        isLoading: false,
    }),
    selectedFacilityId: null,
    isLoading: false,
    error: null,
    searchFilters: {
        cities: [],
        status: [],
    },
    appliedFilters: {
        cities: [],
        status: [],
        operatorEmail: '',
        searchText: '',
    },
};

const facilitiesSlice = createSlice({
    name: 'facilities',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(setFacilitySearchText, (state, {payload}) => {
                state.appliedFilters.searchText = payload;
            })
            .addCase(getFilterOptionsData.fulfilled, (state, {payload}) => {
                state.searchFilters = payload;
            })
            .addCase(updateAppliedFilters, (state, {payload}) => {
                state.appliedFilters = payload;
            })
            .addCase(clearAppliedFilters, (state) => {
                state.appliedFilters = {
                    ...initialState.appliedFilters,
                    status: [],
                };
            })
            .addCase(getPaginatedFacilitiesData.fulfilled, (state, action) => {
                const {payload} = action;
                const {canonicalFacilities, parkingSpots} = payload?.entities;

                facilitiesAdapter.upsertMany(
                    state.canonicalFacilities,
                    canonicalFacilities
                );

                facilitiesAdapter.upsertMany(state.facilities, parkingSpots);

                state.meta = payload.meta;
                state.facilities.isLoading = false;
                state.appliedFilters = {
                    ...state.appliedFilters,
                    ...action.meta.arg.filters,
                };
            })
            .addCase(getPaginatedFacilitiesData.pending, (state) => {
                state.facilities.isLoading = true;
            })
            .addCase(
                getSearchedFacilitiesData.fulfilled,
                (state, {payload}) => {
                    const {canonicalFacilities, parkingSpots} =
                        payload?.entities;

                    facilitiesAdapter.setAll(
                        state.canonicalFacilities,
                        canonicalFacilities
                    );

                    facilitiesAdapter.setAll(state.facilities, parkingSpots);

                    state.meta = payload.meta;
                    state.isLoading = false;
                    state.error = false;
                }
            )
            .addCase(getSearchedFacilitiesData.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(getSearchedFacilitiesData.rejected, (state, {payload}) => {
                state.isLoading = false;
                state.error = payload.message;

                if (payload.message === ERRORS.NO_RESULTS_FOUND) {
                    state.meta = payload.meta;
                }
            })
            .addCase(getInitialFacilities.fulfilled, (state) => {
                state.isLoading = false;
            })
            .addCase(setFilteredFacilities.pending, (state) => {
                state.filteredFacilities.isLoading = true;
            })
            .addCase(getFacilityDetailsData.fulfilled, (state, {payload}) => {
                facilitiesAdapter.upsertOne(state.facilities, payload);

                filteredFacilitiesAdapter.upsertOne(
                    state.filteredFacilities,
                    payload
                );

                state.isLoading = false;
            })
            .addCase(updateSelectedFacilityId, (state, {payload}) => {
                state.selectedFacilityId = payload;
            })
            .addCase(
                updateFacilityHasCreatedEventRates,
                (state, {payload: facilityId}) => {
                    const changes = {hasCreatedEventRates: true};

                    facilitiesAdapter.updateOne(state.facilities, {
                        id: facilityId,
                        changes,
                    });
                    filteredFacilitiesAdapter.updateOne(
                        state.filteredFacilities,
                        {id: facilityId, changes}
                    );
                }
            )
            .addMatcher(
                isAnyOf(
                    setFilteredFacilities.fulfilled,
                    updateFilteredFacilities.fulfilled
                ),
                (state, {payload}) => {
                    filteredFacilitiesAdapter.setAll(
                        state.filteredFacilities,
                        payload
                    );

                    state.filteredFacilities.isLoading = false;
                }
            )
            .addMatcher(isAnyOf(getInitialFacilities.pending), (state) => {
                return {
                    ...state,
                    isLoading: true,
                    error: false,
                };
            })
            .addMatcher(
                isAnyOf(
                    getInitialFacilities.rejected,
                    getFacilityDetailsData.rejected
                ),
                (state) => {
                    return {
                        ...state,
                        isLoading: false,
                        error: true,
                    };
                }
            );
    },
});

export default facilitiesSlice.reducer;
